import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import {
  DocumentManagementSettingsService,
  translateProblem,
} from '@tourfold/features/document-management';
import {
  resolveSettingsLoadErrorDetails,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';

const MIN_RETENTION_DAYS = 1;
const MAX_RETENTION_DAYS = 180;

/**
 * Tenant-wide document-management settings page, registered into the global settings sidebar via
 * `provideSettingsElementDocumentManagement`. Surfaces:
 *   * the auto-hard-delete retention input — backend cron permanently removes soft-deleted
 *     documents whose `soft_deleted_at` is older than this value.
 *
 * Visual structure mirrors `settings-geocoding`: `settings-page` shared classes for content
 * layout and a `tf-settings-actions` footer (Cancel + Save). The page header is owned by the
 * settings shell (title derived from the route), so the page renders content only. All colours
 * come from `--p-*` / `--tf-*` design tokens so dark mode flips correctly.
 */
@Component({
  selector: 'tf-document-management-settings-page',
  imports: [
    TranslateModule,
    FormsModule,
    ButtonModule,
    InputNumberModule,
    MessageModule,
    SettingsActions,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './document-management-settings-page.component.html',
  styleUrl: './document-management-settings-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentManagementSettingsComponent {
  private readonly translate = inject(TranslateService);
  private readonly settingsService = inject(DocumentManagementSettingsService);
  private readonly resourceAccessService = inject(ResourceAccessService);

  protected readonly minRetention = MIN_RETENTION_DAYS;
  protected readonly maxRetention = MAX_RETENTION_DAYS;

  protected readonly canEdit = computed(() =>
    this.resourceAccessService.canPerform('documentManagementSettings', 'update')
  );

  protected readonly autoHardDeleteAfterDays = signal<number | null>(null);
  protected readonly initialValue = signal<number | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly hasLoadedOnce = signal(false);
  protected readonly initialLoadFailed = signal(false);
  protected readonly loadError = signal<string | null>(null);
  protected readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  protected readonly isSaving = signal(false);
  protected readonly saveError = signal<string | null>(null);

  /** True when the form is dirty (current value differs from the persisted baseline). */
  protected readonly isDirty = computed(() => {
    const current = this.autoHardDeleteAfterDays();
    return current !== null && current !== this.initialValue();
  });

  /**
   * Disable the Save button when there's nothing to do or when we're mid-flight. The numeric
   * input is configured with `[min]/[max]` so out-of-range values can only land here via paste —
   * gate Save explicitly so a tampered input never tries a known-bad PATCH.
   */
  protected readonly canSave = computed(() => {
    const current = this.autoHardDeleteAfterDays();
    if (this.isLoading() || this.isSaving() || !this.canEdit()) return false;
    if (!this.isDirty()) return false;
    if (current === null) return false;
    if (current < this.minRetention || current > this.maxRetention) return false;
    return true;
  });

  constructor() {
    void this.load();
  }

  private async load(): Promise<void> {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    try {
      const settings = await this.settingsService.get();
      const value = settings.autoHardDeleteAfterDays ?? null;
      this.autoHardDeleteAfterDays.set(value);
      this.initialValue.set(value);
      this.hasLoadedOnce.set(true);
      this.initialLoadFailed.set(false);
      this.loadError.set(null);
      this.loadErrorDetails.set(null);
    } catch (error: unknown) {
      const message = this.translate.instant('DOCUMENT_MANAGEMENT.SETTINGS_LOAD_FAILED');
      if (hadLoadedOnce) {
        this.saveError.set(message);
      } else {
        this.initialLoadFailed.set(true);
        this.loadError.set(message);
        this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
      }
    }
    this.isLoading.set(false);
  }

  protected onValueChange(next: number | null): void {
    this.autoHardDeleteAfterDays.set(next);
    if (this.saveError()) this.saveError.set(null);
  }

  protected onCancel(): void {
    this.autoHardDeleteAfterDays.set(this.initialValue());
    this.saveError.set(null);
  }

  protected retryLoad(): void {
    void this.load();
  }

  protected clearSaveError(): void {
    this.saveError.set(null);
  }

  protected async onSave(): Promise<void> {
    if (!this.canSave()) return;
    const value = this.autoHardDeleteAfterDays();
    if (value === null) return;
    this.isSaving.set(true);
    this.saveError.set(null);
    try {
      const updated = await this.settingsService.setAutoHardDeleteAfterDays(value);
      const persisted = updated.autoHardDeleteAfterDays ?? value;
      this.autoHardDeleteAfterDays.set(persisted);
      this.initialValue.set(persisted);
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.SETTINGS_SAVE_FAILED'
      );
      this.saveError.set(message);
    } finally {
      this.isSaving.set(false);
    }
  }
}
